import { SideNavItemChild } from 'components/navigation/side_nav/components/SideNavItemChild';
import { NavigationItem } from 'components/navigation/navigation_wrapper/NavigationWrapper';
import { useSideNav } from 'components/navigation/side_nav/context';
import { Collapse, Nav } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import cn from 'classnames';

export type SideNavItemProps = {
    activeChildId: string;
    isActive: boolean;
    isExpanded: boolean;
    item: NavigationItem;
};

export const SideNavItem = (props: SideNavItemProps) => {

    const { activeChildId, isActive, isExpanded, item } = props;

    const { handleNavigate, handleToggleExpand, isCollapsed } = useSideNav();

    const hasChildren = item.children.length > 0;

    // Parent shows indicator when directly active, or when a child is active but the parent is collapsed
    const showParentIndicator = isActive && (!activeChildId || !isExpanded);

    const handleClick = e => {

        hasChildren && handleToggleExpand(item.id);

        handleNavigate(e, item);

    };

    // Parent (expandable) items render as a non-anchor element, so they need explicit
    // keyboard activation. Leaf items render as a real <Link> (anchor) and are already
    // keyboard-operable.
    const handleKeyDown = e => {

        if (hasChildren && (e.key === 'Enter' || e.key === ' ')) {

            e.preventDefault();
            handleClick(e);

        }

    };

    const icon = item.iconLink
        ? <img alt="" className="flex-shrink-0" src={item.iconLink} style={{ height: 16, width: 24 }} />
        : <i aria-hidden="true" className={`fa-light ${item.icon} flex-shrink-0 text-center`} style={{ fontSize: 16, width: 24 }} />;

    const NavComponent = hasChildren ? Nav.Item : Link;

    // Expandable parents behave like buttons; leaf items are links. Only mark the
    // current leaf with aria-current="page".
    const semanticProps = hasChildren
        ? { 'aria-expanded': isExpanded, onKeyDown: handleKeyDown, role: 'button', tabIndex: 0 }
        : { 'aria-current': isActive ? 'page' as const : undefined };

    const parentClassname = cn(
        'side-nav-item nav-item text-decoration-none d-flex align-items-center rounded-2 py-3 gap-3 text-white position-relative fw-semibold',
        {
            'active': isActive,
            'justify-content-center px-2': isCollapsed,
            'no-indicator': !showParentIndicator,
            'px-3': !isCollapsed
        }
    );

    return (
        <>
            <NavComponent
                className={parentClassname}
                onClick={handleClick}
                title={item.label}
                to={item.path}
                {...semanticProps}
            >
                {icon}
                <div
                    style={{
                        columnGap: 20,
                        opacity: isCollapsed ? 0 : 1,
                        transition: 'opacity 200ms'
                    }}
                    className={`${isCollapsed ? 'd-none' : 'd-flex'} justify-content-between align-items-center flex-grow-1`}
                >
                    <span className="text-capitalize" style={{ fontSize: 14 }}>
                        {item.label}
                    </span>
                    {item.linkTarget === 'blank' && <i aria-hidden="true" className="fa-thin fa-arrow-up-right-from-square" />}
                    {hasChildren && (
                        <i
                            style={{
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                                transition: 'transform 200ms'
                            }}
                            aria-hidden="true"
                            className="fa-solid fa-angle-down ms-auto"
                        />
                    )}
                </div>
            </NavComponent>

            {hasChildren && (
                <Collapse in={isExpanded}>
                    <div className="side-nav-submenu ms-3 rounded-2">
                        {!!item.children?.length && item.children.map((child, key) => (
                            <SideNavItemChild
                                activeChildId={activeChildId}
                                item={child}
                                key={`${child.id}-${key}`}
                            />
                        ))}
                    </div>
                </Collapse>
            )}
        </>
    );

};